import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import { Drawer, Portal, Flex } from '@chakra-ui/react';
import { ErrorBoundary } from '@shared';

type DrawerComponent = {
	id: string;
	content: ReactNode;
	zIndex?: number;
	size?: 'md' | 'lg' | 'xl';
	onClose?: () => void;
};

type DrawerContextInterface = {
	openDrawer: (drawer: Omit<DrawerComponent, 'onClose'>) => void;
	closeDrawer: (id: string) => void;
};

const DrawerContext = createContext<DrawerContextInterface | undefined>(undefined);

export function DrawerProvider({ children }: { children: ReactNode }) {
	const [drawers, setDrawers] = useState<Array<DrawerComponent & { isOpen: boolean }>>([]);

	const openDrawer = useCallback((drawer: Omit<DrawerComponent, 'onClose'>) => {
		setDrawers(prev => [...prev.filter(d => d.id !== drawer.id), { ...drawer, isOpen: true }]);
	}, []);

	const closeDrawer = useCallback((id: string) => {
		setDrawers(prev => [
			...prev.map(d => {
				if (d.id === id) return { ...d, isOpen: false };
				return d;
			}),
		]);
	}, []);

	return (
		<DrawerContext.Provider value={{ openDrawer, closeDrawer }}>
			{drawers.map(drawer => {
				return (
					<Portal key={drawer.id}>
						<Drawer.Root
							size={drawer.size || 'lg'}
							key={drawer.id}
							open={drawer.isOpen}
							onOpenChange={e => {
								if (e.open === false) {
									closeDrawer(drawer.id);
								}
							}}
						>
							<Drawer.Backdrop zIndex={drawer.zIndex ? drawer.zIndex : 'modal'} />
							<Drawer.Positioner zIndex={drawer.zIndex ? drawer.zIndex : 'modal'}>
								<Drawer.Content p={5}>
									<ErrorBoundary>
										<Flex flex={1} flexDir="column" position="relative" overflowY="auto" borderRadius="1.25rem" bg="rgba(251, 249, 255, 1)">
											{drawer.content}
										</Flex>
									</ErrorBoundary>
								</Drawer.Content>
							</Drawer.Positioner>
						</Drawer.Root>
					</Portal>
				);
			})}
			{children}
		</DrawerContext.Provider>
	);
}

export function useDrawer() {
	const context = useContext(DrawerContext);
	if (!context) {
		throw new Error('useDrawer must be used within a DrawerProvider');
	}
	return context;
}
